/**
 * Keeps a loaner-device line in sync with subscription lines in the cart.
 *
 * The tracker ships once but the contract bills monthly, so hardware and service
 * must be two separate lines: only lines carrying a selling plan end up in the
 * subscription contract, one-time lines are fulfilled once and never recur.
 *
 * Strategy: wrap `fetch` and reconcile after every cart mutation, then hand the
 * updated section markup back to the caller so the theme's own re-render already
 * shows the final cart. Config comes from `snippets/subscription-loaner.liquid`.
 *
 * Which lines qualify is decided in Liquid, not here: products carrying the
 * metafield `custom.leihgeraet_inklusive` render a hidden line property
 * (`_leihgeraet`) into the add-to-cart form, and that property is what we count.
 */

const configElement = document.getElementById('ef-loaner-config');

if (configElement?.textContent) {
  const config = JSON.parse(configElement.textContent);
  const loanerVariantId = String(config.loanerVariantId);
  const triggerPropertyKey = config.triggerPropertyKey;

  if (triggerPropertyKey) {
    const originalFetch = window.fetch.bind(window);
    const CART_MUTATIONS = [config.cartAddUrl, config.cartChangeUrl, config.cartUpdateUrl];

    /** Serialises reconciliation so rapid clicks cannot add the loaner twice. */
    let pending = Promise.resolve();

    /**
     * @param {RequestInfo | URL} input
     * @returns {string}
     */
    const resolveUrl = (input) => {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.pathname;
      return input?.url ?? '';
    };

    /** @param {string} url */
    const isCartMutation = (url) => {
      const path = url.split('?')[0]?.replace(/\.js$/, '') ?? '';
      return CART_MUTATIONS.some((route) => route && path.endsWith(route));
    };

    /**
     * The caller asked for re-rendered sections; request the same ones so the
     * response we hand back stays a drop-in replacement.
     * @param {RequestInit} [init]
     * @returns {string | undefined}
     */
    const extractSections = (init) => {
      const body = init?.body;
      if (body instanceof FormData) {
        const values = body.getAll('sections').filter(Boolean);
        return values.length ? String(values[values.length - 1]) : undefined;
      }
      if (typeof body === 'string') {
        try {
          return JSON.parse(body).sections;
        } catch {
          return new URLSearchParams(body).get('sections') ?? undefined;
        }
      }
      return undefined;
    };

    /**
     * @param {string} url
     * @param {Record<string, unknown>} payload
     */
    const postJson = async (url, payload) => {
      const response = await originalFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      return response.json();
    };

    const getCart = async () => {
      const response = await originalFetch(`${config.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });
      return response.json();
    };

    /**
     * Brings the loaner line to the quantity implied by the subscription lines.
     * @param {string} [sections]
     * @returns {Promise<Record<string, unknown> | null>} the mutation response, or null if nothing changed
     */
    const reconcile = async (sections) => {
      const cart = await getCart();
      const items = cart.items ?? [];

      // Deliberately not gated on a selling plan: the 3-year term is sold as a
      // prepaid one-time order (no contract, see shopify-article-structure.md §3)
      // and still ships hardware. The metafield alone decides.
      const required = items
        .filter((item) => item.properties?.[triggerPropertyKey])
        .reduce((total, item) => total + item.quantity, 0);

      const loanerLine = items.find((item) => String(item.variant_id) === loanerVariantId);
      const current = loanerLine?.quantity ?? 0;

      if (required === current) return null;

      if (!loanerLine) {
        return postJson(config.cartAddUrl, {
          items: [
            {
              id: Number(loanerVariantId),
              quantity: required,
              properties: { [config.loanerPropertyKey]: config.loanerLabel },
            },
          ],
          sections,
        });
      }

      // change.js keeps the line properties that add.js set on the first pass
      return postJson(config.cartChangeUrl, { id: loanerLine.key, quantity: required, sections });
    };

    window.fetch = async function patchedFetch(input, init) {
      const response = await originalFetch(input, init);

      if (!response.ok || !isCartMutation(resolveUrl(input))) return response;

      const original = response.clone();

      try {
        const sections = extractSections(init);
        const run = pending.then(() => reconcile(sections));
        pending = run.catch(() => {});
        const result = await run;

        if (!result) return response;

        const originalBody = await original.json();

        // Errors surface as a `status` field; never mask one with our own result.
        if (originalBody.status) return response;

        const merged = { ...originalBody, sections: result.sections ?? originalBody.sections };

        // Fresh headers on purpose: the original Content-Length no longer matches.
        return new Response(JSON.stringify(merged), {
          status: response.status,
          statusText: response.statusText,
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (error) {
        // A broken loaner sync must never break add-to-cart or checkout.
        console.warn('[EDGEFleet] Leihgerät-Abgleich fehlgeschlagen:', error);
        return response;
      }
    };
  }
}
